import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { CustomersService } from '../../services/customers.service';
import * as CustomersActions from '../actions/customers.actions';
import { selectCustomersLoaded } from '../selectors/customers.selectors';

@Injectable()
export class CustomersEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly customersService = inject(CustomersService);

    ensureCustomersLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomersActions.ensureCustomersLoaded),
            withLatestFrom(this.store.select(selectCustomersLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => CustomersActions.loadCustomers())
        )
    );

    loadCustomers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomersActions.loadCustomers),
            switchMap(() =>
                this.customersService.getCustomers().pipe(
                    map(customers => CustomersActions.loadCustomersSuccess({ customers })),
                    catchError(err => of(CustomersActions.loadCustomersFailure({ error: err?.message || 'Load customers failed' })))
                )
            )
        )
    );

    createCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomersActions.createCustomer),
            switchMap(({ customer }) =>
                this.customersService.createCustomer(customer).pipe(
                    map(created => CustomersActions.createCustomerSuccess({ customer: created })),
                    catchError(err => of(CustomersActions.createCustomerFailure({ error: err?.message || 'Create customer failed' })))
                )
            )
        )
    );

    updateCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomersActions.updateCustomer),
            switchMap(({ id, changes }) =>
                this.customersService.updateCustomer(id, changes).pipe(
                    map(updated => CustomersActions.updateCustomerSuccess({ customer: updated })),
                    catchError(err => of(CustomersActions.updateCustomerFailure({ error: err?.message || 'Update customer failed' })))
                )
            )
        )
    );

    deleteCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomersActions.deleteCustomer),
            switchMap(({ id }) =>
                this.customersService.deleteCustomer(id).pipe(
                    map(() => CustomersActions.deleteCustomerSuccess({ id })),
                    catchError(err => of(CustomersActions.deleteCustomerFailure({ error: err?.message || 'Delete customer failed' })))
                )
            )
        )
    );
}
